import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import Dialog from '@mui/material/Dialog'
import LinearProgress from '@mui/material/LinearProgress'
import { Role } from 'common/api/v1/types'
import { useUser } from '../../../utils'
import { parseAddApplianceAction } from '../../../utils/routes'
import { AddApplianceDialog, type ApplianceType } from '../../appliances/AddApplianceDialog'
import { GlobalSearchInput } from './GlobalSearchInput'
import { GlobalSearchResults } from './GlobalSearchResults'
import { useGlobalSearch } from './useGlobalSearch'

interface GlobalSearchContextValue {
  open: () => void
  close: () => void
  isOpen: boolean
}

const GlobalSearchContext = createContext<GlobalSearchContextValue>({
  open: () => undefined,
  close: () => undefined,
  isOpen: false,
})

export const useGlobalSearchDialog = () => useContext(GlobalSearchContext)

export const GlobalSearchProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [addApplianceType, setAddApplianceType] = useState<ApplianceType | null>(null)
  const navigate = useNavigate()
  const user = useUser()
  const isBasicUser = user.role === Role.basic
  const { categories, loading } = useGlobalSearch(searchTerm)

  const open = useCallback(() => {
    if (!isBasicUser) setIsOpen(true)
  }, [isBasicUser])
  const close = useCallback(() => {
    setIsOpen(false)
    setSearchTerm('')
    setSelectedIndex(0)
  }, [])

  useEffect(() => {
    if (isBasicUser) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isBasicUser])

  useEffect(() => {
    setSelectedIndex(0)
  }, [categories])

  // Flat list of navigable items: each category header (if navigable) followed by its results
  const flatItems = categories.flatMap((c) => [
    ...(c.navigateTo ? [c.navigateTo] : []),
    ...c.results.map((r) => r.navigateTo),
  ])

  const handleSelect = (target: string) => {
    const applianceType = parseAddApplianceAction(target)
    if (applianceType) {
      setAddApplianceType(applianceType)
    } else {
      navigate(target)
    }
    close()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (flatItems.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % flatItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const navigateTo = flatItems[selectedIndex]
      if (navigateTo) handleSelect(navigateTo)
    }
  }

  return (
    <GlobalSearchContext.Provider value={{ open, close, isOpen }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={close}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              position: 'fixed',
              top: '15%',
              margin: 0,
            },
          },
        }}
      >
        <GlobalSearchInput value={searchTerm} onChange={setSearchTerm} onKeyDown={handleKeyDown} />
        {loading && <LinearProgress />}
        <GlobalSearchResults
          categories={categories}
          selectedIndex={selectedIndex}
          onSelect={handleSelect}
          hasSearchTerm={searchTerm.length >= 2}
          loading={loading}
        />
      </Dialog>
      {addApplianceType && (
        <AddApplianceDialog
          user={user}
          open={true}
          onClose={() => setAddApplianceType(null)}
          applianceType={addApplianceType}
        />
      )}
    </GlobalSearchContext.Provider>
  )
}
